import { authFetch } from '@/api/client'

export type OAuthPlatform = 'youtube' | 'tiktok' | 'instagram'

/**
 * Start connecting a YouTube/TikTok/Instagram account to a channel, then leave
 * for the platform's consent page.
 *
 * The flow used to start on a plain link to ``/auth/<platform>?channel=``,
 * which the backend served to anyone, for any channel: from the internet,
 * someone could link their own account to another owner's channel. It now
 * starts on an authenticated POST that checks the caller owns the channel
 * and returns the consent URL, bound server-side to a one-time ``state``.
 */
export async function startOAuth(platform: OAuthPlatform, channel: string): Promise<void> {
  // ``include``: the API sets a flow cookie (path: its callback) that ties
  // the consent coming back to this browser; in production the API is
  // another origin, and only a credentialed fetch keeps that cookie.
  const res = await authFetch(`/api/v1/channels/${encodeURIComponent(channel)}/${platform}/connect`, {
    method: 'POST',
    credentials: 'include',
  })
  const body = (await res.json().catch(() => ({}))) as { data?: { auth_url?: string }; detail?: string }
  const authUrl = body.data?.auth_url
  if (isMissingRoute(res.status, body.detail)) {
    throw new Error(
      `Este servidor ainda não sabe conectar o ${PLATFORM_LABELS[platform]}: a conexão chega com o próximo deploy do backend.`,
    )
  }
  if (!res.ok || !authUrl) {
    throw new Error(body.detail ?? `Falha ao iniciar a conexão (HTTP ${String(res.status)})`)
  }
  window.location.assign(authUrl)
}

const PLATFORM_LABELS: Record<OAuthPlatform, string> = { youtube: 'YouTube', tiktok: 'TikTok', instagram: 'Instagram' }

/**
 * A rota ainda não existe neste backend — o front sai no merge, o backend no
 * pull do skab (e o Instagram chega num PR próprio). O 404 de rota inexistente
 * vem com o ``detail`` padrão do FastAPI, ``"Not Found"``; o de canal
 * desconhecido traz o próprio texto e segue para a tela como está.
 */
export function isMissingRoute(status: number, detail: unknown): boolean {
  return (status === 404 || status === 405) && (detail === undefined || detail === 'Not Found' || detail === 'Method Not Allowed')
}

/** ``startOAuth`` for a click handler: a failure becomes the message shown. */
export function startOAuthOrReport(platform: OAuthPlatform, channel: string, report: (message: string) => void): void {
  startOAuth(platform, channel).catch((err: unknown) => {
    report(err instanceof Error ? err.message : 'Falha ao iniciar a conexão')
  })
}
